import { useMemo } from "react";
import { ContainerMediaType } from "@/src/store/apis/tasks/taskDetail/interface";
import { PhotoTypeSelectionState } from "../types";
import { useSelection } from "./useSelection";

/**
 * Photo Type 선택 상태를 관리하는 커스텀 훅
 *
 * 내부적으로 제네릭 useSelection 훅을 사용하여 구현됩니다.
 * Set을 사용한 O(1) 조회로 성능을 최적화하고,
 * allPhotoTypes 변경 시 stale selection을 자동으로 정리합니다.
 *
 * @param allPhotoTypes - 선택 가능한 모든 Photo Type 배열
 * @returns PhotoTypeSelectionState - 선택 상태 및 조작 함수들
 *
 * @example
 * const availableTypes: ContainerMediaType[] = [
 *   'SAMPLE_BALE', 'EMPTY', 'QUARTER_LOADED', 'HALF_LOADED',
 *   'FULL_LOADED', 'ONE_DOOR_CLOSED', 'BOTH_DOORS_CLOSED', 'CLOSE_UP_SEAL', 'EXTRA'
 * ];
 * const selection = usePhotoTypeSelection(availableTypes);
 *
 * // 개별 선택/해제
 * selection.togglePhotoType('SAMPLE_BALE');
 *
 * // 전체 선택/해제
 * selection.addAllPhotoTypes();
 * selection.removeAllPhotoTypes();
 *
 * // 선택된 타입 배열
 * console.log(selection.selectedPhotoTypes); // ['SAMPLE_BALE', 'EMPTY']
 */
export const usePhotoTypeSelection = (
  allPhotoTypes: ContainerMediaType[]
): PhotoTypeSelectionState => {
  const {
    selectedItems,
    addItem,
    removeItem,
    addAllItems,
    removeAllItems,
    toggleItem,
    isAllSelected,
  } = useSelection(allPhotoTypes);

  // PhotoType-specific naming으로 매핑
  return useMemo(
    () => ({
      selectedPhotoTypes: selectedItems,
      addPhotoType: addItem,
      removePhotoType: removeItem,
      addAllPhotoTypes: addAllItems,
      removeAllPhotoTypes: removeAllItems,
      togglePhotoType: toggleItem,
      isAllSelected,
    }),
    [
      selectedItems,
      addItem,
      removeItem,
      addAllItems,
      removeAllItems,
      toggleItem,
      isAllSelected,
    ]
  );
};
